import { Box, Divider, Flex, Heading, Text } from '@chakra-ui/react';
import { Dispatch, FunctionComponent, SetStateAction, useMemo } from 'react';
import { FileUpload, SelectField, SelectReadOnlyField } from '@/components';
import { ClientUploadConfig } from '@/constants';
import {  setSelectedWaveOption, useAppDispatch, useAppSelector } from '@/redux';
import { SelectedUploadFile } from '@/types/upload';
import { useDataProcessingJob } from '../../useDataProcessingJob';

type Props = {
  uploadConfig: ClientUploadConfig;
  files: SelectedUploadFile[];
  setFiles: Dispatch<SetStateAction<SelectedUploadFile[]>>;
  isUploading: boolean;
};

const SECTION_HEADING_PROPS = {
  as: 'h2' as const,
  fontSize: '16px',
  fontWeight: '600',
  color: 'marketcast.text.primary',
};

/** What the user provides for this wave: wave, description and data files. */
export const WaveDetailsPanel: FunctionComponent<Props> = ({ uploadConfig, files, setFiles, isUploading }) => {
  const dispatch = useAppDispatch();
  const selectedClient = useAppSelector((state) => state.wave.selectedClient);
  const selectedWaveOption = useAppSelector((state) => state.wave.selectedWaveOption);
  const { isRunning, isSucceeded } = useDataProcessingJob();

  // A running or finished job describes these inputs, so they can't change under it.
  const isLocked = isRunning || isSucceeded;

  const waveOptions = useMemo(
    () => (selectedClient?.waveNumber ?? []).map((wave) => ({ id: wave, name: wave })),
    [selectedClient]
  );

  return (
    <Box
      bg="marketcast.base.backgroundAlt"
      borderRadius="12px"
      border="1px solid"
      borderColor="marketcast.gray.5"
      boxShadow="sm"
      p={6}
    >
      <Box as="section" aria-labelledby="wave-details-heading">
        <Heading id="wave-details-heading" {...SECTION_HEADING_PROPS} mb={1}>
          Wave details
        </Heading>
        <Text fontSize="13px" color="marketcast.gray.2" mb={4}>
          Select the wave this data belongs to.
        </Text>
        <Flex direction="column" gap={4}>
          {isLocked ? (
            <SelectReadOnlyField label="Wave" value={selectedWaveOption} />
          ) : (
            <SelectField
              label="Wave"
              value={selectedWaveOption}
              onChange={(value) => {
                dispatch(setSelectedWaveOption(value));
              }}
              options={waveOptions}
            />
          )}
        </Flex>
      </Box>

      <Divider my={6} borderColor="marketcast.gray.5" />

      <Box as="section" aria-labelledby="files-heading">
        <Flex justify="space-between" align="baseline" mb={1}>
          <Heading id="files-heading" {...SECTION_HEADING_PROPS}>
            Files
          </Heading>
          <Text fontSize="12px" color="marketcast.gray.2">
            {files.length} / {uploadConfig.maxFiles}
          </Text>
        </Flex>
        <Text fontSize="13px" color="marketcast.gray.2" mb={4}>
          Add the data files for this wave, then upload them from Step progress.
        </Text>
        <FileUpload
          allowedExtensions={uploadConfig.allowedExtensions}
          maxFiles={uploadConfig.maxFiles}
          files={files}
          onFilesChange={setFiles}
          showList
          isDisabled={isUploading || isLocked}
        />
      </Box>
    </Box>
  );
};
